import MakulaLogo from "@shared/svg/makula-logo-dark.svg?react";
import BodyText, { BODY_TEXT_SIZES } from "@shared/ui/BodyText";
import Button, { BUTTON_VARIANTS } from "@shared/ui/Button";
import Headline, { HEADLINE_SIZES } from "@shared/ui/Headline";
import { Input } from "@shared/ui/Inputs";
import { Eye, EyeSlash } from "phosphor-react";
import React, { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useIntl } from "react-intl";
import { Link } from "react-router-dom";

import ContentLoading from "~/components/ContentLoading";

const emailPattern =
  /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;

const LoginForm = ({ modifier, callback = () => {}, color }) => {
  const { messages: msg } = useIntl();
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const {
    register,
    handleSubmit,
    setError,
    clearErrors,
    watch,
    formState: { errors },
  } = useForm();

  const onSubmit = (data) => {
    setLoading(true);
    callback(data, setError);
  };

  const onError = () => {
    setLoading(false);
  };

  useEffect(() => {
    let mounted = true;

    if (mounted) {
      clearErrors("root");
      setLoading(false);
    }

    return () => {
      mounted = false;
    };
  }, [errors.root]);

  const emailField = register("email", {
    required: {
      value: true,
      message: msg?.common?.errors.emailRequired,
    },
    pattern: {
      value: emailPattern,
      message: msg?.login?.email.invalidEmail,
    },
  });

  const passwordField = register("password", {
    required: {
      value: true,
      message: msg?.login?.password.invalidPassword,
    },
  });

  return (
    <form
      onSubmit={handleSubmit(onSubmit, onError)}
      className="flex flex-col gap-lg"
    >
      <MakulaLogo className="h-[48px] w-fit" />

      <div className="flex flex-col gap-2 mb-4">
        <Headline size={HEADLINE_SIZES.MEDIUM} className="mb-xs">
          {msg.login?.loginLabel}
        </Headline>
        <BodyText size={BODY_TEXT_SIZES.SMALL} color="text-secondary">
          {msg.login?.loginSubTitle}
        </BodyText>
      </div>

      <Input
        keyId="email"
        label={msg.login?.email.label}
        name="email"
        type="text"
        data-testId="login-form-email"
        placeholder={msg.login?.email.placeholder}
        {...emailField}
        setRef={emailField.ref}
        errorMessage={errors?.email?.message}
        customInputClasses={
          errors?.email?.message
            ? "bg-primary border-danger focus:bg-primary focus:border-danger"
            : watch("email")
            ? "bg-primary border-primary"
            : "bg-accent border-transparent"
        }
      />

      <Input
        keyId="password"
        label={msg.login?.password.label}
        name="password"
        data-testId="login-form-password"
        type={showPassword ? "text" : "password"}
        trailingIconConfig={{
          icon: showPassword ? (
            <Eye onClick={() => setShowPassword(!showPassword)} />
          ) : (
            <EyeSlash onClick={() => setShowPassword(!showPassword)} />
          ),
        }}
        placeholder={msg.login?.password.placeholder}
        {...passwordField}
        setRef={passwordField.ref}
        errorMessage={errors?.password?.message}
        customInputClasses={
          errors?.password?.message
            ? "bg-primary border-danger focus:bg-primary focus:border-danger"
            : watch("password")
            ? "bg-primary border-primary"
            : "bg-accent border-transparent"
        }
      />

      <div className="flex justify-end -mt-sm">
        <Link to={`/forgot-password${modifier ? "/" + modifier : ""}`}>
          <Button
            variant={BUTTON_VARIANTS.LINK}
            type="button"
            text={msg.login?.forgotPassword.title}
          />
        </Link>
      </div>

      <Button
        fullWidth={true}
        text={loading ? <ContentLoading /> : `${msg.login?.loginLabel}`}
        data-testId="login-form-submit"
        type="submit"
        disabled={loading}
      />

      <BodyText
        size={BODY_TEXT_SIZES.X_SMALL}
        color="text-secondary"
        className="text-center mt-sm"
      >
        {msg.login?.terms?.prefix}{" "}
        <span
          className="font-bold cursor-pointer hover:underline"
          onClick={() =>
            window.open("https://makula.io/terms-condition", "_blank")
          }
        >
          {msg.login?.terms?.ampersand} <br />
          {msg.login?.terms?.conditions}
        </span>{" "}
        {msg.login?.terms?.and}{" "}
        <span
          className="font-bold cursor-pointer hover:underline"
          onClick={() =>
            window.open("https://makula.io/privacy-policy", "_blank")
          }
        >
          {msg.login?.terms?.privacy}
        </span>
      </BodyText>
    </form>
  );
};

export default LoginForm;
